import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import {
  Prompt,
  PromptType,
  PROMPT_TYPE_KOREAN_NAMES,
  getPromptTypeKoreanName,
  AVAILABLE_AI_MODELS,
} from "common/models/types";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Input from "components/atoms/Input";
import Icon from "components/atoms/Icon";
import Tag from "components/atoms/Tag";
import Switch from "components/atoms/Switch";
import Dropdown, { DropdownMenuItem } from "components/molecules/Dropdown";
import { savePrompt, togglePromptActive, fetchActivePrompt } from "api/promptService";
import { ToastManager } from "components/organisms/CommonUI";
import {
  isDoseExtractPromptType,
  isAllowedDoseExtractModel,
  DEFAULT_DOSE_EXTRACT_MODEL,
} from "common/models/doseExtractPrompt";
import { log } from "utils/LogUtil";
import { LogLevel } from "utils/LogUtil";

interface PromptInfoProps {
  prompt: Prompt | null;
  onEdit?: () => void;
  onUpdate?: (prompt: Prompt) => void;
  onReload?: (promptId: number) => void;
  onDelete?: (promptId: number) => void;
  isLoading?: boolean;
}

// 컨테이너 스타일
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

// 헤더 스타일
const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  display: flex;
  align-items: center;
`;

// 내용 영역 스타일
const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

// 편집 영역 스타일
const EditContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  flex: 1;
  overflow-y: auto;
`;

// 빈 상태 메시지 스타일
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px 16px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

// 폼 그룹 스타일
const FormGroup = styled.div``;

// dose 추출 타입일 때 모델 칸 아래 안내 한 줄(D-6).
const DoseModelHint = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

// 폼 라벨 스타일
const Label = styled.label`
  display: block;
  margin-bottom: 2px;
  font-weight: 600;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

// 액션 버튼 영역 스타일
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

// 메타 정보 스타일
const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
`;

// 메타 항목 스타일
const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 프롬프트 내용 스타일
const PromptContent = styled.div`
  white-space: pre-wrap;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", "source-code-pro", monospace;
  font-size: 14px;
  line-height: 1.6;
  overflow-x: auto;
`;

// 프롬프트 타입 선택 컨테이너
const PromptTypeSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  max-height: 120px;
  overflow-y: auto;
  padding: 4px;
`;

// 프롬프트 타입 옵션
const PromptTypeOption = styled.div<{ isSelected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding: 8px 18px;
  margin: 0 4px 8px 0;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background-color: ${({ isSelected }) =>
    isSelected ? COLOR_SYSTEM.get("Blue")?.[100] : COLOR_SYSTEM.get("Gray")?.[50]};
  color: ${({ isSelected }) =>
    isSelected ? COLOR_SYSTEM.get("Blue")?.[700] : COLOR_SYSTEM.get("Gray")?.[700]};
  border: 1.5px solid
    ${({ isSelected }) =>
      isSelected ? COLOR_SYSTEM.get("Blue")?.[400] : COLOR_SYSTEM.get("Gray")?.[200]};
  box-shadow: ${({ isSelected }) => (isSelected ? "0 2px 8px rgba(0, 80, 255, 0.08)" : "none")};

  transition: background 0.15s, border 0.15s, color 0.15s;

  &:hover {
    background-color: ${({ isSelected }) =>
      isSelected ? COLOR_SYSTEM.get("Blue")?.[200] : COLOR_SYSTEM.get("Gray")?.[100]};
    border-color: ${({ isSelected }) =>
      isSelected ? COLOR_SYSTEM.get("Blue")?.[500] : COLOR_SYSTEM.get("Gray")?.[300]};
  }
`;

// 프롬프트 타입 그룹 스타일
const PromptTypeGroup = styled.div`
  margin-top: 12px;
  width: 100%;
  max-height: 160px;
  overflow-y: auto;
`;

// 프롬프트 타입 레이블 스타일
const PromptTypeLabel = styled.div`
  font-size: 11px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin-bottom: 4px;
`;

// 텍스트 영역 스타일 (설명용 - 작게)
const DescriptionTextArea = styled.textarea`
  width: 100%;
  min-height: 60px;
  max-height: 120px;
  padding: 8px 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 4px;
  font-family: inherit;
  font-size: 13px;
  resize: vertical;
  overflow-y: auto;

  &:focus {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[400]};
    outline: none;
  }
`;

// 텍스트 영역 스타일 (프롬프트 내용용 - 크게)
const PromptTextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  height: calc(100vh - 350px);
  max-height: calc(100vh - 350px);
  padding: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 4px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", "source-code-pro", monospace;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  overflow-y: auto;

  &:focus {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[400]};
    outline: none;
  }
`;

// ContentEditable 에디터 스타일
const PromptEditor = styled.div`
  width: 100%;
  min-height: 200px;
  height: calc(100vh - 350px);
  max-height: calc(100vh - 350px);
  padding: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 4px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", "source-code-pro", monospace;
  font-size: 14px;
  line-height: 1.5;
  overflow-y: auto;
  background-color: white;
  white-space: pre-wrap;
  word-wrap: break-word;

  &:focus {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[400]};
    outline: none;
  }

  /* XML 태그 스타일링 */
  .xml-tag-open {
    color: ${COLOR_SYSTEM.get("Purple")?.[400]};
    font-weight: bold;
  }

  .xml-tag-close {
    color: ${COLOR_SYSTEM.get("Blue")?.[400]};
    font-weight: bold;
  }

  .xml-tag-name {
    color: ${COLOR_SYSTEM.get("Blue")?.[400]};
    font-weight: bold;
  }

  /* XML 태그 depth별 색상 */
  .xml-tag-depth-0 {
    color: ${COLOR_SYSTEM.get("Blue")?.[400]}; /* 파란색 - 최상위 */
    font-weight: bold;
    margin-left: 0px; /* 최상위 - 들여쓰기 없음 */
  }

  .xml-tag-depth-1 {
    color: ${COLOR_SYSTEM.get("Green")?.[400]}; /* 초록색 - 1단계 */
    font-weight: bold;
    margin-left: 16px; /* 1단계 - 16px 들여쓰기 */
  }

  .xml-tag-depth-2 {
    color: ${COLOR_SYSTEM.get("Skyblue")?.[700]}; /* 하늘색 - 2단계 */
    font-weight: bold;
    margin-left: 32px; /* 2단계 - 32px 들여쓰기 */
  }

  .xml-tag-depth-3 {
    color: ${COLOR_SYSTEM.get("Orange")?.[400]}; /* 주황색 - 3단계 */
    font-weight: bold;
    margin-left: 48px; /* 3단계 - 48px 들여쓰기 */
  }

  .xml-tag-depth-4 {
    color: ${COLOR_SYSTEM.get("Purple")?.[400]}; /* 보라색 - 4단계 */
    font-weight: bold;
    margin-left: 64px; /* 4단계 - 64px 들여쓰기 */
  }

  .xml-tag-depth-5 {
    color: ${COLOR_SYSTEM.get("Brown")?.[400]}; /* 갈색 - 5단계 */
    font-weight: bold;
    margin-left: 80px; /* 5단계 - 80px 들여쓰기 */
  }

  .xml-tag-depth-6 {
    color: ${COLOR_SYSTEM.get("Skyblue")?.[400]}; /* 하늘색 - 6단계 */
    font-weight: bold;
    margin-left: 96px; /* 6단계 - 96px 들여쓰기 */
  }

  /* 7단계 이상은 순환 */
  .xml-tag-depth-default {
    color: ${COLOR_SYSTEM.get("Gray")?.[600]}; /* 회색 - 기본 */
    font-weight: bold;
    margin-left: 112px; /* 기본 - 112px 들여쓰기 */
  }

  /* 플레이스홀더 스타일 */
  &:empty:before {
    content: attr(data-placeholder);
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
    pointer-events: none;
  }

  /* 마크다운 헤더 레벨별 색상 (해시 + 내용) */
  .markdown-level-1 {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[600]}; /* # + 제목 - 빨간색 */
    color: ${COLOR_SYSTEM.get("Gray")?.[0]};
    font-weight: bold;
  }

  .markdown-level-2 {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[500]}; /* ## + 제목 - 주황색 */
    color: ${COLOR_SYSTEM.get("Gray")?.[0]};
    font-weight: bold;
  }

  .markdown-level-3 {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[400]}; /* ### + 제목 - 노란색 */
    color: ${COLOR_SYSTEM.get("Gray")?.[0]};
    font-weight: bold;
  }

  .markdown-level-4 {
    background-color: ${COLOR_SYSTEM.get("Green")?.[400]}; /* #### + 제목 - 초록색 */
    font-weight: bold;
  }

  .markdown-level-5 {
    background-color: ${COLOR_SYSTEM.get("Blue")?.[400]}; /* ##### + 제목 - 파란색 */
    font-weight: bold;
  }

  .markdown-level-6 {
    background-color: ${COLOR_SYSTEM.get("Purple")?.[400]}; /* ###### + 제목 - 보라색 */
    font-weight: bold;
  }
`;

// 프롬프트 타입 & 버전 한 줄 배치용 스타일
const RowFormGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;
const InlineLabel = styled(Label)`
  margin-bottom: 4px;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.2;
  height: 16px;
  display: block;
`;
const TypeDropdownWrap = styled.div`
  min-width: 160px;
  flex: 0 0 160px;
`;
const VersionInputWrap = styled.div`
  min-width: 120px;
  flex: 0 0 120px;

  /* Input 컴포넌트 커스텀 스타일 */
  .custom-input {
    min-width: unset;

    /* InputWrapper 높이 조정 */
    > div:last-child {
      height: 40px;
      padding: 8.5px 12px;
    }
  }
`;

// 모델 선택 드롭다운 스타일
const ModelDropdownWrap = styled.div`
  min-width: 200px;
  flex: 0 0 200px;
`;

// 2분할 레이아웃 스타일
const SplitContainer = styled.div`
  display: flex;
  gap: 16px;
  height: 100%;
`;

const LeftPanel = styled.div`
  flex: 0 0 30%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 300px;
`;

const RightPanel = styled.div`
  flex: 0 0 70%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 16px;
  border-left: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  min-width: 400px;
`;

// 태그 구조 인터페이스
interface TagNode {
  name: string;
  level: number;
  startIndex: number;
  endIndex: number;
  children: TagNode[];
  content?: string;
}

// 목차 컴포넌트 스타일
const TableOfContents = styled.div`
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
  padding: 16px;
  max-height: 580px;
  overflow-y: auto;
`;

const TOCTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  margin-bottom: 12px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  padding-bottom: 8px;
`;

const TOCItem = styled.div<{
  level: number;
  isClickable: boolean;
  isHovered: boolean;
  isParentHovered: boolean;
  isActive: boolean;
}>`
  padding: 4px 8px;
  margin-left: ${({ level }) => level * 16}px;
  font-size: 12px;
  color: ${({ isClickable }) =>
    isClickable ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[600]};
  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;

  background-color: ${({ isHovered, isParentHovered, isActive, isClickable }) => {
    if (isActive && isClickable) return COLOR_SYSTEM.get("Green")?.[100];
    if (isHovered && isClickable) return COLOR_SYSTEM.get("Blue")?.[100];
    if (isParentHovered && isClickable) return COLOR_SYSTEM.get("Blue")?.[30];
    return "transparent";
  }};

  border-left: ${({ isHovered, isParentHovered, isActive }) => {
    if (isActive) return `3px solid ${COLOR_SYSTEM.get("Green")?.[400]}`;
    if (isHovered) return `3px solid ${COLOR_SYSTEM.get("Blue")?.[400]}`;
    if (isParentHovered) return `2px solid ${COLOR_SYSTEM.get("Blue")?.[200]}`;
    return "3px solid transparent";
  }};

  &:hover {
    background-color: ${({ isClickable, isActive }) => {
      if (isActive && isClickable) return COLOR_SYSTEM.get("Green")?.[150];
      if (isClickable) return COLOR_SYSTEM.get("Blue")?.[50];
      return "transparent";
    }};
  }

  /* 레벨 표시 */
  &::after {
    content: ${({ isHovered, isActive, level }) => {
      if (isActive || isHovered) return `'Lv.${level}'`;
      return "''";
    }};
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    color: ${({ isActive }) =>
      isActive ? COLOR_SYSTEM.get("Green")?.[600] : COLOR_SYSTEM.get("Blue")?.[400]};
    background-color: ${({ isActive }) =>
      isActive ? COLOR_SYSTEM.get("Green")?.[50] : COLOR_SYSTEM.get("Blue")?.[50]};
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: 600;
  }
`;

// 태그 분석 함수
const parsePromptTags = (content: string): TagNode[] => {
  if (!content) return [];

  const tags: TagNode[] = [];
  // 더 정확한 XML 태그 매칭 - 태그명만 정확히 매칭
  const tagRegex = /<(\/?)([\w\-_]+)(?:\s[^>]*)?>|<(\/?)([\w\-_]+)>/g;
  const allMatches: Array<{
    type: "open" | "close";
    name: string;
    index: number;
    fullMatch: string;
  }> = [];

  let match;

  // 모든 태그를 순서대로 수집
  while ((match = tagRegex.exec(content)) !== null) {
    const isClosing = match[1] === "/" || match[3] === "/";
    const tagName = match[2] || match[4];

    // 유효한 태그명인지 확인 (알파벳, 숫자, 하이픈, 언더스코어만)
    if (tagName && /^[\w\-_]+$/.test(tagName)) {
      allMatches.push({
        type: isClosing ? "close" : "open",
        name: tagName,
        index: match.index,
        fullMatch: match[0],
      });
    }
  }

  // 스택을 사용하여 태그 매칭 - 올바른 스택 처리
  const stack: Array<{
    name: string;
    startIndex: number;
    level: number;
  }> = [];

  for (const tagMatch of allMatches) {
    if (tagMatch.type === "open") {
      // 시작 태그
      const level = stack.length;
      stack.push({
        name: tagMatch.name,
        startIndex: tagMatch.index,
        level: level,
      });
    } else {
      // 닫는 태그 - 스택에서 가장 최근의 매칭되는 시작 태그 찾기
      for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].name === tagMatch.name) {
          const startTag = stack[i];
          const endIndex = tagMatch.index + tagMatch.fullMatch.length;

          // 태그 내용 추출
          const tagContent = content.substring(startTag.startIndex, endIndex);

          tags.push({
            name: tagMatch.name,
            level: startTag.level,
            startIndex: startTag.startIndex,
            endIndex: endIndex,
            children: [],
            content: tagContent,
          });

          // 매칭된 시작 태그부터 스택 끝까지 모두 제거 (올바른 중첩 처리)
          stack.splice(i);
          break;
        }
      }
    }
  }

  // 시작 위치별로 정렬
  return tags.sort((a, b) => a.startIndex - b.startIndex);
};

// 일반 텍스트에서 제목 추출 (마크다운 스타일)
const parseMarkdownHeaders = (content: string): TagNode[] => {
  if (!content) return [];

  const headers: TagNode[] = [];
  const lines = content.split("\n");
  let currentPosition = 0;

  // 1단계: 모든 헤더 위치 찾기
  const headerPositions: Array<{
    level: number;
    title: string;
    lineIndex: number;
    startIndex: number;
    line: string;
  }> = [];

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("#")) {
      const level = trimmed.match(/^#+/)?.[0].length || 1;
      const title = trimmed.replace(/^#+\s*/, "");

      if (title) {
        const hashIndex = line.indexOf("#");
        const lineStart = currentPosition + hashIndex;

        headerPositions.push({
          level: level - 1, // 0부터 시작하도록 조정
          title,
          lineIndex: index,
          startIndex: lineStart,
          line,
        });
      }
    }

    currentPosition += line.length + 1;
  });

  // 2단계: 각 헤더의 끝 위치 계산 (다음 같은 레벨 이상의 헤더까지)
  headerPositions.forEach((header, index) => {
    let endPosition = content.length; // 기본값: 문서 끝

    // 다음 헤더들 중에서 같은 레벨이거나 상위 레벨 헤더 찾기
    for (let i = index + 1; i < headerPositions.length; i++) {
      const nextHeader = headerPositions[i];
      if (nextHeader.level <= header.level) {
        // 다음 헤더의 시작 위치를 현재 헤더의 끝으로 설정
        let nextHeaderPosition = 0;
        for (let j = 0; j <= nextHeader.lineIndex - 1; j++) {
          nextHeaderPosition += lines[j].length + 1;
        }
        endPosition = nextHeaderPosition - 1; // 줄바꿈 직전까지
        break;
      }
    }

    headers.push({
      name: header.title,
      level: header.level,
      startIndex: header.startIndex,
      endIndex: endPosition,
      children: [],
      content: header.line,
    });
  });

  return headers;
};

// XML 태그 안에 있는 마크다운 헤더의 depth 조정 함수
const adjustMarkdownDepthInXML = (xmlTags: TagNode[], markdownHeaders: TagNode[]): TagNode[] => {
  return markdownHeaders.map((header) => {
    // 이 마크다운 헤더를 포함하는 모든 XML 태그들을 찾기
    const containingXMLTags = xmlTags.filter(
      (xmlTag) => header.startIndex >= xmlTag.startIndex && header.startIndex <= xmlTag.endIndex
    );

    if (containingXMLTags.length === 0) {
      // XML 태그 안에 없으면 원래 레벨 유지
      return header;
    }

    // 가장 깊은 중첩 레벨 찾기 (가장 작은 범위를 가진 태그)
    const deepestXMLTag = containingXMLTags.reduce((deepest, current) => {
      const currentSize = current.endIndex - current.startIndex;
      const deepestSize = deepest.endIndex - deepest.startIndex;
      return currentSize < deepestSize ? current : deepest;
    });

    // 마크다운 헤더의 끝 위치를 포함하는 XML 태그 범위로 제한
    let adjustedEndIndex = header.endIndex;
    if (adjustedEndIndex > deepestXMLTag.endIndex) {
      adjustedEndIndex = deepestXMLTag.endIndex;
    }

    // XML 태그의 실제 중첩 depth 계산
    const xmlDepth = containingXMLTags.length - 1; // 실제 중첩된 XML 태그 개수

    // 조정된 레벨: XML depth + 1 + 마크다운 원래 레벨
    const adjustedLevel = xmlDepth + 1 + header.level;

    return {
      ...header,
      level: adjustedLevel,
      endIndex: adjustedEndIndex,
    };
  });
};

const PromptInfo: React.FC<PromptInfoProps> = ({
  prompt,
  onEdit,
  onUpdate,
  onReload,
  onDelete,
  isLoading,
}) => {
  // 편집 중인 프롬프트 상태
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  // 저장 중 상태
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // 모델 드롭다운 상태
  const [showModelDropdown, setShowModelDropdown] = useState<boolean>(false);
  // 프롬프트 타입 드롭다운 상태
  const [showTypeDropdown, setShowTypeDropdown] = useState<boolean>(false);
  // 목차 호버 상태
  const [hoveredTOCIndex, setHoveredTOCIndex] = useState<number | null>(null);
  // 현재 커서 위치에 해당하는 목차 항목 상태 추가
  const [activeTOCIndex, setActiveTOCIndex] = useState<number | null>(null);
  // 입력 디바운스용 타이머 ref
  const inputTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // dose 타입 전환 시 활성 모델 조회의 경합 토큰 — 늦게 도착한 응답을 버리기 위함(AC-42f).
  const doseModelFetchToken = useRef(0);
  // 에디터 강제 업데이트를 위한 ref
  const editorForceUpdateRef = useRef<() => void>();
  // 저장 직전 편집기 DOM의 최신 textContent를 읽기 위한 노드 ref (D-2)
  const editorNodeRef = useRef<HTMLDivElement | null>(null);

  // HTML 이스케이프로 인한 위치 변화 계산 헬퍼 함수
  const calculateEscapedPosition = useCallback((original: string, originalPos: number): number => {
    let escapedPos = 0;
    let originalIndex = 0;

    while (originalIndex < originalPos && originalIndex < original.length) {
      const char = original[originalIndex];
      if (char === "&") {
        escapedPos += 5; // &amp;
      } else if (char === "<") {
        escapedPos += 4; // &lt;
      } else if (char === ">") {
        escapedPos += 4; // &gt;
      } else {
        escapedPos += 1;
      }
      originalIndex++;
    }

    return escapedPos;
  }, []);

  // 정확한 태그 위치 찾기 헬퍼 함수
  const findExactTagPosition = useCallback(
    (highlighted: string, searchTag: string, approximatePos: number): number => {
      // 근사 위치 주변에서 정확한 태그 찾기
      const searchStart = Math.max(0, approximatePos - 50);
      const searchEnd = Math.min(highlighted.length, approximatePos + 200);
      const searchArea = highlighted.substring(searchStart, searchEnd);

      const foundIndex = searchArea.indexOf(searchTag);
      if (foundIndex !== -1) {
        return searchStart + foundIndex;
      }

      // 근사 위치에서 찾지 못한 경우 전체 텍스트에서 검색
      return highlighted.indexOf(searchTag);
    },
    []
  );

  // 구문 하이라이팅 함수
  const highlightSyntax = useCallback(
    (text: string): string => {
      if (!text) return "";

      // HTML 이스케이프
      let highlighted = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

      // 모든 하이라이팅 대상을 수집하여 위치별로 정렬 후 처리
      const highlights: Array<{
        start: number;
        end: number;
        replacement: string;
        type: "xml" | "markdown-header";
      }> = [];

      // 1. XML 태그 수집 - 목차와 동일한 로직 사용
      const xmlTags = parsePromptTags(text);

      // 2. 각 태그의 정확한 위치에서 하이라이팅 적용
      xmlTags.forEach((tag) => {
        if (!tag.content) return;

        // 원본 텍스트에서 실제 시작 태그와 끝 태그 추출
        const tagContentLines = tag.content.split("\n");
        const firstLine = tagContentLines[0];
        const lastLine = tagContentLines[tagContentLines.length - 1];

        // 시작 태그 추출 (첫 번째 줄에서 > 까지)
        const startTagEnd = firstLine.indexOf(">");
        if (startTagEnd === -1) return;

        const originalStartTag = firstLine.substring(0, startTagEnd + 1);
        const originalEndTag = `</${tag.name}>`;

        // HTML 이스케이프된 버전으로 변환
        const escapedStartTag = originalStartTag
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        const escapedEndTag = originalEndTag
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");

        // 이스케이프된 텍스트에서의 근사 위치 계산
        const approximateStartPos = calculateEscapedPosition(text, tag.startIndex);
        const approximateEndPos = calculateEscapedPosition(
          text,
          tag.endIndex - originalEndTag.length
        );

        // 정확한 위치에서 태그 찾기
        const startTagIndex = findExactTagPosition(
          highlighted,
          escapedStartTag,
          approximateStartPos
        );
        const endTagIndex = findExactTagPosition(highlighted, escapedEndTag, approximateEndPos);

        const depthClass = tag.level <= 6 ? `xml-tag-depth-${tag.level}` : "xml-tag-depth-default";

        // 시작 태그 하이라이팅 추가
        if (startTagIndex !== -1) {
          highlights.push({
            start: startTagIndex,
            end: startTagIndex + escapedStartTag.length,
            replacement: `<span class="${depthClass}">${escapedStartTag}</span>`,
            type: "xml",
          });
        }

        // 끝 태그 하이라이팅 추가
        if (endTagIndex !== -1) {
          highlights.push({
            start: endTagIndex,
            end: endTagIndex + escapedEndTag.length,
            replacement: `<span class="${depthClass}">${escapedEndTag}</span>`,
            type: "xml",
          });
        }
      });

      // 3. 마크다운 헤더 수집
      const lines = highlighted.split("\n");
      let currentPos = 0;

      lines.forEach((line, lineIndex) => {
        const trimmed = line.trim();
        if (trimmed.startsWith("#")) {
          const hashMatch = line.match(/^(\s*)(#{1,6})(\s+)(.*)$/);
          if (hashMatch) {
            const [, leadingSpace, hashes, space, content] = hashMatch;
            const lineStart = currentPos + leadingSpace.length;
            const lineEnd = currentPos + line.length;

            // 전체 마크다운 헤더 라인을 하나로 하이라이팅
            if (content) {
              const fullHeaderContent = `${hashes}${space}${content}`;
              const escapedContent = fullHeaderContent
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");

              highlights.push({
                start: lineStart,
                end: lineEnd,
                replacement: `<span class="markdown-level-${hashes.length}">${escapedContent}</span>`,
                type: "markdown-header",
              });
            }
          }
        }
        currentPos += line.length + 1; // +1 for newline
      });

      // 4. 겹치지 않는 하이라이팅만 적용 (XML 우선, 마크다운 후순위)
      highlights.sort((a, b) => a.start - b.start);

      // 겹치는 영역 제거 (XML이 우선)
      const validHighlights: typeof highlights = [];
      for (const highlight of highlights) {
        const hasOverlap = validHighlights.some(
          (existing) => highlight.start < existing.end && highlight.end > existing.start
        );

        if (!hasOverlap) {
          validHighlights.push(highlight);
        }
      }

      // 5. 뒤에서부터 적용 (인덱스 변화 방지)
      validHighlights.sort((a, b) => b.start - a.start);
      for (const highlight of validHighlights) {
        highlighted =
          highlighted.substring(0, highlight.start) +
          highlight.replacement +
          highlighted.substring(highlight.end);
      }

      return highlighted;
    },
    [calculateEscapedPosition, findExactTagPosition]
  );

  // 새 프롬프트인지 확인
  const isNewPrompt = prompt && prompt.id === 0;

  // 컴포넌트 마운트 또는 프롬프트 변경 시 체크
  React.useEffect(() => {
    if (prompt) {
      setEditingPrompt({ ...prompt });
    }
  }, [prompt]);

  // 컴포넌트 언마운트 시 타이머 정리
  React.useEffect(() => {
    return () => {
      if (inputTimeoutRef.current) {
        clearTimeout(inputTimeoutRef.current);
      }
    };
  }, []);

  // 입력값 변경 핸들러
  const handleInputChange = useCallback(
    (field: keyof Prompt, value: any) => {
      if (editingPrompt) {
        setEditingPrompt((prev) => (prev ? { ...prev, [field]: value } : null));
      }
    },
    [editingPrompt]
  );

  // 프롬프트 타입 변경 핸들러
  const handlePromptTypeChange = useCallback(
    (type: PromptType) => {
      if (!editingPrompt) return;
      setEditingPrompt((prev) => (prev ? { ...prev, promptType: type } : null));

      // dose 추출 타입으로 바꾸면 모델 칸을 그 타입의 현재 활성 프롬프트 모델로 채운다(D-6).
      // 활성이 없거나 모델이 허용 목록 밖이면 Sonnet으로 채운다. 다른 타입은 모델을 건드리지 않는다.
      if (!isDoseExtractPromptType(type)) return;
      const token = ++doseModelFetchToken.current;
      const rowId = editingPrompt.id;
      void (async () => {
        let model: string = DEFAULT_DOSE_EXTRACT_MODEL;
        try {
          const active = await fetchActivePrompt(type);
          if (active?.model && isAllowedDoseExtractModel(active.model)) {
            model = active.model;
          }
        } catch {
          // 활성 조회 실패(네트워크·404·백엔드 미배포·403)도 기본 모델로 — 편집기를 막지 않는다.
          model = DEFAULT_DOSE_EXTRACT_MODEL;
        }
        // 늦게 도착한 응답 폐기: 이 요청 이후 (행 id, 타입)이 바뀌었으면 버린다(AC-42f).
        if (token !== doseModelFetchToken.current) return;
        setEditingPrompt((prev) => {
          if (!prev || prev.id !== rowId || prev.promptType !== type) return prev;
          return { ...prev, model };
        });
      })();
    },
    [editingPrompt]
  );

  // 모델 선택 핸들러
  const handleModelSelect = useCallback(
    (model: string) => {
      if (editingPrompt) {
        setEditingPrompt((prev) => (prev ? { ...prev, model } : null));
        setShowModelDropdown(false);
      }
    },
    [editingPrompt]
  );

  // 저장 핸들러
  const handleSave = useCallback(async () => {
    if (editingPrompt) {
      try {
        setIsSaving(true);

        // D-2: 대기 중인 디바운스 입력을 즉시 반영한다.
        // 마지막 입력 뒤 100ms 안에 저장을 눌러도 그 입력이 빠지지 않도록,
        // 대기 타이머를 지우고 편집기 DOM의 현재 textContent를 payload에 반영한다.
        if (inputTimeoutRef.current) {
          clearTimeout(inputTimeoutRef.current);
          inputTimeoutRef.current = null;
        }
        const latestTemplate =
          editorNodeRef.current?.textContent ?? editingPrompt.promptTemplate;
        const promptToSave: Prompt = { ...editingPrompt, promptTemplate: latestTemplate };
        // 상태도 같은 값으로 갱신(화면과 payload 일치)
        setEditingPrompt(promptToSave);

        // id가 0인 경우: 새로운 프롬프트 생성 (POST 요청)
        // id가 있는 경우: 기존 프롬프트 업데이트 (PATCH 요청)
        const updatedPrompt = await savePrompt(promptToSave);

        if (onUpdate) {
          // 서버에서 반환한 업데이트된 프롬프트 정보로 상태 업데이트
          onUpdate(updatedPrompt);
        }

        // 저장 후 에디터의 구문 하이라이팅 강제 업데이트
        setTimeout(() => {
          if (editorForceUpdateRef.current) {
            editorForceUpdateRef.current();
          }
        }, 100);

        setIsSaving(false);
      } catch (error) {
        // 저장 거절(dose 타입 모델·표식 검증 실패 등) 봉투 message를 토스트로 보인다(기존 고정 alert 폐기).
        log(LogLevel.ERROR, "프롬프트 저장 실패:", error);
        ToastManager.show({
          content: error instanceof Error ? error.message : "프롬프트 저장에 실패했습니다.",
          duration: 3000,
        });
        setIsSaving(false);
      }
    }
  }, [editingPrompt, onUpdate]);

  // 활성화 토글 핸들러
  const handleToggleActive = useCallback(async () => {
    if (prompt && prompt.id) {
      try {
        // 로딩 상태 표시
        setIsSaving(true);

        // 프롬프트 활성화 상태 변경
        await togglePromptActive(prompt.id, !prompt.isActive);

        // 목록과 상세 정보 다시 로드
        if (onReload) {
          onReload(prompt.id);
        } else if (onUpdate) {
          // onReload가 없는 경우 기존 방식으로 fallback
          const updatedPrompt = { ...prompt, isActive: !prompt.isActive };
          onUpdate(updatedPrompt);
        }

        setIsSaving(false);
      } catch (error) {
        log(LogLevel.ERROR, "프롬프트 상태 변경 실패:", error);
        alert("프롬프트 상태 변경에 실패했습니다.");
        setIsSaving(false);
      }
    }
  }, [prompt, onUpdate, onReload]);

  // 삭제 핸들러
  const handleDelete = useCallback(async () => {
    if (prompt && prompt.id && prompt.id !== 0 && onDelete) {
      // 삭제 확인
      const isConfirmed = window.confirm(
        `정말로 이 프롬프트를 삭제하시겠습니까?\n\n` +
          `타입: ${getPromptTypeKoreanName(prompt.promptType)}\n` +
          `버전: ${prompt.version}\n` +
          `설명: ${prompt.description || "없음"}\n\n` +
          `이 작업은 되돌릴 수 없습니다.`
      );

      if (isConfirmed) {
        try {
          setIsSaving(true);
          await onDelete(prompt.id);
        } catch (error) {
          log(LogLevel.ERROR, "프롬프트 삭제 실패:", error);
          alert("프롬프트 삭제에 실패했습니다.");
        } finally {
          setIsSaving(false);
        }
      }
    }
  }, [prompt, onDelete]);

  // 목차 생성
  const tableOfContents = useMemo(() => {
    if (!editingPrompt?.promptTemplate) return [];

    const content = editingPrompt.promptTemplate;
    const allItems: TagNode[] = [];

    // 1. XML 태그 파싱
    const xmlTags = parsePromptTags(content);
    allItems.push(...xmlTags);

    // 2. 마크다운 헤더 파싱
    const markdownHeaders = parseMarkdownHeaders(content);

    // 3. XML 태그 안에 있는 마크다운 헤더의 depth 조정
    const adjustedMarkdownHeaders = adjustMarkdownDepthInXML(xmlTags, markdownHeaders);
    allItems.push(...adjustedMarkdownHeaders);

    // 4. 시작 위치별로 정렬
    return allItems.sort((a, b) => a.startIndex - b.startIndex);
  }, [editingPrompt?.promptTemplate]);

  // 현재 커서 위치에 해당하는 목차 항목 찾기
  const findActiveTOCItem = useCallback((cursorPosition: number, items: TagNode[]) => {
    let bestMatch: { index: number; level: number } | null = null;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (cursorPosition >= item.startIndex && cursorPosition <= item.endIndex) {
        // 현재 위치가 이 태그 범위 안에 있으면서
        // 이전에 찾은 것보다 더 깊은 레벨(더 구체적)이면 업데이트
        if (!bestMatch || item.level > bestMatch.level) {
          bestMatch = { index: i, level: item.level };
        }
      }
    }

    return bestMatch ? bestMatch.index : null;
  }, []);

  // ContentEditable에서 실제 텍스트 위치 계산
  const getTextPositionFromSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;

    const range = selection.getRangeAt(0);
    const editor = document.querySelector(".prompt-editor") as HTMLDivElement;
    if (!editor) return 0;

    // 선택 위치까지의 텍스트 길이 계산
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editor);
    preCaretRange.setEnd(range.startContainer, range.startOffset);

    return preCaretRange.toString().length;
  }, []);

  // ContentEditable에서 텍스트 범위 선택
  const selectTextRange = useCallback((startPos: number, endPos: number) => {
    const editor = document.querySelector(".prompt-editor") as HTMLDivElement;
    if (!editor) return;

    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);

    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    let currentPos = 0;
    let startNode: Text | null = null;
    let startOffset = 0;
    let endNode: Text | null = null;
    let endOffset = 0;

    for (const textNode of textNodes) {
      const nodeLength = textNode.textContent?.length || 0;

      // 시작 위치 찾기
      if (!startNode && currentPos + nodeLength >= startPos) {
        startNode = textNode;
        startOffset = startPos - currentPos;
      }

      // 끝 위치 찾기
      if (!endNode && currentPos + nodeLength >= endPos) {
        endNode = textNode;
        endOffset = endPos - currentPos;
        break;
      }

      currentPos += nodeLength;
    }

    if (startNode && endNode) {
      const range = document.createRange();
      const selection = window.getSelection();

      range.setStart(startNode, Math.min(startOffset, startNode.textContent?.length || 0));
      range.setEnd(endNode, Math.min(endOffset, endNode.textContent?.length || 0));

      selection?.removeAllRanges();
      selection?.addRange(range);

      // 선택된 영역으로 스크롤
      const rect = range.getBoundingClientRect();
      const editorRect = editor.getBoundingClientRect();

      if (rect.top < editorRect.top || rect.bottom > editorRect.bottom) {
        editor.scrollTop += rect.top - editorRect.top - 50;
      }
    }
  }, []);

  // 프롬프트 텍스트 변경 및 커서 위치 추적 핸들러
  const handlePromptTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPosition = e.target.selectionStart;

      // 프롬프트 내용 업데이트
      handleInputChange("promptTemplate", newValue);

      // 현재 커서 위치에 해당하는 목차 항목 찾기
      if (tableOfContents.length > 0) {
        const activeIndex = findActiveTOCItem(cursorPosition, tableOfContents);
        setActiveTOCIndex(activeIndex);

        // 목차 스크롤
        if (activeIndex !== null) {
          setTimeout(() => {
            const tocContainer = document.querySelector(".toc-container") as HTMLElement;
            const activeItem = document.querySelector(`.toc-item-${activeIndex}`) as HTMLElement;

            if (tocContainer && activeItem) {
              const containerRect = tocContainer.getBoundingClientRect();
              const itemRect = activeItem.getBoundingClientRect();

              // 목차 컨테이너 내에서의 상대적 위치 계산
              const relativeTop = itemRect.top - containerRect.top + tocContainer.scrollTop;
              const targetScrollTop = relativeTop - tocContainer.clientHeight / 3;

              tocContainer.scrollTop = Math.max(0, targetScrollTop);
            }
          }, 0);
        }
      }
    },
    [handleInputChange, tableOfContents, findActiveTOCItem]
  );

  // ContentEditable 입력 핸들러
  const handleEditorInput = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const text = target.textContent || "";

      // 프롬프트 내용 업데이트 (debounce 적용)
      clearTimeout(inputTimeoutRef.current);
      inputTimeoutRef.current = setTimeout(() => {
        handleInputChange("promptTemplate", text);
      }, 100);

      // 커서 위치 계산 (정확한 구현)
      const cursorPosition = getTextPositionFromSelection();

      // 현재 커서 위치에 해당하는 목차 항목 찾기
      if (tableOfContents.length > 0) {
        const activeIndex = findActiveTOCItem(cursorPosition, tableOfContents);
        setActiveTOCIndex(activeIndex);

        // 목차 스크롤
        if (activeIndex !== null) {
          setTimeout(() => {
            const tocContainer = document.querySelector(".toc-container") as HTMLElement;
            const activeItem = document.querySelector(`.toc-item-${activeIndex}`) as HTMLElement;

            if (tocContainer && activeItem) {
              const containerRect = tocContainer.getBoundingClientRect();
              const itemRect = activeItem.getBoundingClientRect();

              const relativeTop = itemRect.top - containerRect.top + tocContainer.scrollTop;
              const targetScrollTop = relativeTop - tocContainer.clientHeight / 3;

              tocContainer.scrollTop = Math.max(0, targetScrollTop);
            }
          }, 0);
        }
      }
    },
    [handleInputChange, tableOfContents, findActiveTOCItem, getTextPositionFromSelection]
  );

  // ContentEditable 키 입력 핸들러
  const handleEditorKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Tab 키 처리
    if (e.key === "Tab") {
      e.preventDefault();
      document.execCommand("insertText", false, "  "); // 2칸 들여쓰기
      return;
    }

    // Ctrl+Z (실행 취소) 허용
    if (e.ctrlKey && e.key === "z") {
      // 브라우저 기본 실행 취소 동작 허용
      return;
    }

    // Ctrl+Y (다시 실행) 허용
    if (e.ctrlKey && e.key === "y") {
      // 브라우저 기본 다시 실행 동작 허용
      return;
    }

    // 기타 기본 단축키들 허용 (Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X 등)
    if (e.ctrlKey || e.metaKey) {
      return;
    }
  }, []);

  // ContentEditable 클릭 핸들러
  const handleEditorClick = useCallback(() => {
    // 클릭 후 약간의 지연을 두고 커서 위치 확인
    setTimeout(() => {
      const cursorPosition = getTextPositionFromSelection();

      if (tableOfContents.length > 0) {
        const activeIndex = findActiveTOCItem(cursorPosition, tableOfContents);
        setActiveTOCIndex(activeIndex);

        // 목차 스크롤
        if (activeIndex !== null) {
          const tocContainer = document.querySelector(".toc-container") as HTMLElement;
          const activeItem = document.querySelector(`.toc-item-${activeIndex}`) as HTMLElement;

          if (tocContainer && activeItem) {
            const containerRect = tocContainer.getBoundingClientRect();
            const itemRect = activeItem.getBoundingClientRect();

            const relativeTop = itemRect.top - containerRect.top + tocContainer.scrollTop;
            const targetScrollTop = relativeTop - tocContainer.clientHeight / 3;

            tocContainer.scrollTop = Math.max(0, targetScrollTop);
          }
        }
      }
    }, 10);
  }, [getTextPositionFromSelection, tableOfContents, findActiveTOCItem]);

  // 에디터 내용 업데이트
  const editorRef = useCallback(
    (node: HTMLDivElement | null) => {
      // 저장 직전 최신 본문을 읽을 수 있도록 노드를 보관(D-2)
      editorNodeRef.current = node;
      if (node && editingPrompt?.promptTemplate !== undefined) {
        const currentText = node.textContent || "";
        const expectedText = editingPrompt.promptTemplate;

        // 강제 업데이트 함수 등록
        editorForceUpdateRef.current = () => {
          const highlighted = highlightSyntax(editingPrompt.promptTemplate);
          node.innerHTML = highlighted;
        };

        // 텍스트 내용이 다를 때만 업데이트
        if (currentText !== expectedText) {
          const highlighted = highlightSyntax(editingPrompt.promptTemplate);

          // 현재 커서 위치를 텍스트 기준으로 저장
          const selection = window.getSelection();
          let cursorOffset = 0;

          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(node);
            preCaretRange.setEnd(range.startContainer, range.startOffset);
            cursorOffset = preCaretRange.toString().length;
          }

          // 내용 업데이트
          node.innerHTML = highlighted;

          // 커서 위치 복원
          if (cursorOffset > 0) {
            const textContent = node.textContent || "";
            const targetOffset = Math.min(cursorOffset, textContent.length);

            // 텍스트 노드들을 순회하면서 올바른 위치 찾기
            const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);

            let currentOffset = 0;
            let targetNode: Node | null = null;
            let targetNodeOffset = 0;

            let textNode;
            while ((textNode = walker.nextNode())) {
              const nodeLength = textNode.textContent?.length || 0;

              if (currentOffset + nodeLength >= targetOffset) {
                targetNode = textNode;
                targetNodeOffset = targetOffset - currentOffset;
                break;
              }

              currentOffset += nodeLength;
            }

            // 커서 위치 설정
            if (targetNode) {
              try {
                const newRange = document.createRange();
                const newSelection = window.getSelection();

                newRange.setStart(
                  targetNode,
                  Math.min(targetNodeOffset, targetNode.textContent?.length || 0)
                );
                newRange.collapse(true);

                newSelection?.removeAllRanges();
                newSelection?.addRange(newRange);
              } catch (e) {
                // 커서 위치 복원 실패 시 무시
                console.warn("커서 위치 복원 실패:", e);
              }
            }
          }
        }
      }
    },
    [editingPrompt?.promptTemplate, highlightSyntax]
  );

  // 목차 항목 클릭 핸들러 (ContentEditable용)
  const handleTOCClick = useCallback(
    (item: TagNode, index: number) => {
      const editor = document.querySelector(".prompt-editor") as HTMLDivElement;
      if (editor) {
        editor.focus();

        // 선택할 텍스트 범위 결정
        let selectionStart = item.startIndex;
        let selectionEnd = item.endIndex;

        // XML 태그인지 확인
        const isXMLTag = item.content && item.content.includes("<");

        if (isXMLTag) {
          // XML 태그인 경우 전체 태그를 선택
          selectionStart = item.startIndex;
          selectionEnd = item.endIndex;
        } else {
          // 마크다운 헤더인 경우 해당 줄 전체를 선택
          const textContent = editor.textContent || "";
          const lines = textContent.split("\n");
          const lineIndex = item.startIndex;

          if (lineIndex < lines.length) {
            let lineStart = 0;
            for (let i = 0; i < lineIndex; i++) {
              lineStart += lines[i].length + 1;
            }
            const lineEnd = lineStart + lines[lineIndex].length;

            selectionStart = lineStart;
            selectionEnd = lineEnd;
          }
        }

        // selectTextRange 함수를 사용하여 텍스트 선택
        selectTextRange(selectionStart, selectionEnd);

        // 클릭한 항목을 활성화 상태로 설정
        setActiveTOCIndex(index);
      }
    },
    [selectTextRange]
  );

  // 상위 구조 확인 함수
  const isParentOf = useCallback((parentIndex: number, childIndex: number, items: TagNode[]) => {
    if (parentIndex >= childIndex) return false;

    const parentLevel = items[parentIndex].level;
    const childLevel = items[childIndex].level;

    if (parentLevel >= childLevel) return false;

    // parentIndex와 childIndex 사이에 같은 레벨이나 더 낮은 레벨이 있는지 확인
    for (let i = parentIndex + 1; i < childIndex; i++) {
      if (items[i].level <= parentLevel) return false;
    }

    return true;
  }, []);

  // 프롬프트가 없는 경우
  if (!prompt) {
    return (
      <Container>
        <EmptyState>
          <Icon name="Document" size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <Text textType="H3">선택된 프롬프트가 없습니다</Text>
          <Text textType="Body2" style={{ marginTop: 8 }}>
            왼쪽에서 프롬프트를 선택하거나 새 프롬프트를 작성하세요.
          </Text>
        </EmptyState>
      </Container>
    );
  }

  // 프롬프트 타입 이름 가져오기
  const getPromptTypeName = (type: PromptType | undefined) => {
    return getPromptTypeKoreanName(type);
  };

  return (
    <Container>
      <Header>
        <div style={{ marginRight: "20px" }}>
          <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[700]}>
            프롬프트 편집
            <br />
          </Text>
          {editingPrompt?.id !== 0 && (
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]} style={{ marginTop: 4 }}>
              ID: {editingPrompt?.id}
            </Text>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
            활성화
          </Text>
          <Switch
            size="Medium"
            isActive={editingPrompt?.isActive || false}
            onClick={() => handleInputChange("isActive", !editingPrompt?.isActive)}
          />
        </div>
      </Header>

      <EditContent>
        <SplitContainer>
          <LeftPanel>
            {/* 프롬프트 타입 & 버전 & 기본 모델 한 줄 배치 */}
            <RowFormGroup>
              <TypeDropdownWrap>
                <InlineLabel>프롬프트 타입</InlineLabel>
                <Dropdown
                  defaultValue="타입 선택"
                  selected={getPromptTypeName(editingPrompt?.promptType) || "타입 선택"}
                  onClick={() => setShowTypeDropdown((prev) => !prev)}
                  active={showTypeDropdown}
                  size="Medium"
                >
                  {Object.values(PromptType).map((type) => (
                    <DropdownMenuItem
                      key={type}
                      onClick={() => {
                        handlePromptTypeChange(type);
                        setShowTypeDropdown(false);
                      }}
                      isSelected={editingPrompt?.promptType === type}
                    >
                      <Text textType="Body1">{getPromptTypeName(type)}</Text>
                    </DropdownMenuItem>
                  ))}
                </Dropdown>
              </TypeDropdownWrap>
              <VersionInputWrap>
                <InlineLabel>버전</InlineLabel>
                <Input
                  className="custom-input"
                  value={editingPrompt?.version || ""}
                  onChange={(e) => handleInputChange("version", e.target.value)}
                  placeholder="예: v1.0.0"
                />
              </VersionInputWrap>
              <ModelDropdownWrap>
                <InlineLabel>기본 모델</InlineLabel>
                <Dropdown
                  defaultValue="모델 선택"
                  selected={editingPrompt?.model || ""}
                  onClick={() => setShowModelDropdown(!showModelDropdown)}
                  active={showModelDropdown}
                  size="Medium"
                >
                  {AVAILABLE_AI_MODELS.map((model) => (
                    <DropdownMenuItem
                      key={model}
                      onClick={() => handleModelSelect(model)}
                      isSelected={editingPrompt?.model === model}
                    >
                      <Text textType="Body1">{model}</Text>
                    </DropdownMenuItem>
                  ))}
                </Dropdown>
              </ModelDropdownWrap>
            </RowFormGroup>

            {isDoseExtractPromptType(editingPrompt?.promptType) && (
              <DoseModelHint>
                이 모델로 룰 추출이 실행됩니다 · 허용: Sonnet 4.6 / Opus 4.6 · 같은 타입에 활성이 여러 개면 ID가 가장 큰 것이 쓰입니다
              </DoseModelHint>
            )}

            <FormGroup>
              <Label>설명</Label>
              <DescriptionTextArea
                value={editingPrompt?.description || ""}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="프롬프트에 대한 설명을 입력하세요"
              />
            </FormGroup>

            {/* 목차 */}
            {tableOfContents.length > 0 && (
              <FormGroup>
                <Label>프롬프트 목차</Label>
                <TableOfContents className="toc-container">
                  <TOCTitle>
                    {(() => {
                      const hasXMLTags = tableOfContents.some(
                        (item) => item.content && item.content.includes("<")
                      );
                      const hasMarkdownHeaders = tableOfContents.some(
                        (item) => item.content && item.content.includes("#")
                      );

                      if (hasXMLTags && hasMarkdownHeaders) {
                        return "프롬프트 구조 (XML 태그 + 마크다운)";
                      } else if (hasXMLTags) {
                        return "XML 태그 구조";
                      } else if (hasMarkdownHeaders) {
                        return "마크다운 제목";
                      } else {
                        return "프롬프트 구조";
                      }
                    })()}
                  </TOCTitle>
                  {tableOfContents.map((item, index) => (
                    <TOCItem
                      key={index}
                      className={`toc-item-${index}`}
                      level={item.level}
                      isClickable={true}
                      isHovered={hoveredTOCIndex === index}
                      isParentHovered={
                        hoveredTOCIndex !== null &&
                        isParentOf(index, hoveredTOCIndex, tableOfContents)
                      }
                      isActive={activeTOCIndex === index}
                      onMouseEnter={() => setHoveredTOCIndex(index)}
                      onMouseLeave={() => setHoveredTOCIndex(null)}
                      onClick={() => handleTOCClick(item, index)}
                    >
                      {item.name}
                    </TOCItem>
                  ))}
                </TableOfContents>
              </FormGroup>
            )}
          </LeftPanel>

          <RightPanel>
            <FormGroup>
              <Label>프롬프트 내용</Label>
              <PromptEditor
                className="prompt-editor"
                contentEditable="plaintext-only"
                suppressContentEditableWarning={true}
                ref={editorRef}
                onInput={handleEditorInput}
                onKeyDown={handleEditorKeyDown}
                onClick={handleEditorClick}
                data-placeholder="프롬프트 내용을 입력하세요"
              />
            </FormGroup>
          </RightPanel>
        </SplitContainer>
      </EditContent>

      <Actions>
        {/* 삭제 버튼 - 새 프롬프트가 아닌 경우에만 표시 */}
        {prompt && prompt.id !== 0 && onDelete && (
          <Button
            buttonType="button"
            color="Negative"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={handleDelete}
            disabled={isSaving}
          >
            삭제
          </Button>
        )}
        <div style={{ flex: 1 }} />
        <Button
          buttonType="button"
          color="Secondary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => {
            if (onEdit) onEdit();
          }}
          disabled={isSaving}
        >
          취소
        </Button>
        <Button
          buttonType="button"
          color="Primary"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? "저장 중..." : "저장"}
        </Button>
      </Actions>
    </Container>
  );
};

export default PromptInfo;
